import React from "react";

/**
 * Fehler-Overlay: verhindert „weiße Seiten". Render-Fehler und nicht
 * abgefangene Promise-/Laufzeitfehler werden sichtbar gemacht, statt die
 * App stumm auf eine leere Seite abstürzen zu lassen.
 */

interface ErrInfo {
  message: string;
  stack?: string | undefined;
}

function formatError(err: unknown): ErrInfo {
  if (err instanceof Error) {
    return { message: err.message, stack: err.stack };
  }
  if (typeof err === "string") return { message: err };
  try {
    return { message: JSON.stringify(err) };
  } catch {
    return { message: String(err) };
  }
}

function OverlayBox({ title, message, stack }: { title: string; message: string; stack?: string | undefined }) {
  return (
    <div
      role="alert"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483647,
        background: "var(--gradient-deep, #061b2a)",
        color: "#e6f4f7",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        padding: "1.5rem",
        overflow: "auto",
        WebkitOverflowScrolling: "touch",
      }}
    >
      <div style={{ maxWidth: "720px", margin: "2rem auto 0" }}>
        <div
          style={{
            fontSize: "0.78rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#22d3ee",
            marginBottom: "0.75rem",
          }}
        >
          {title}
        </div>
        <p style={{ fontSize: "1.05rem", fontWeight: 600, margin: "0 0 1rem", lineHeight: 1.5 }}>
          {message}
        </p>
        {stack && (
          <pre
            style={{
              fontSize: "0.78rem",
              lineHeight: 1.5,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              background: "rgba(0,0,0,0.35)",
              border: "1px solid rgba(230,244,247,0.12)",
              borderRadius: "12px",
              padding: "1rem",
              margin: 0,
              color: "rgba(230,244,247,0.8)",
            }}
          >
            {stack}
          </pre>
        )}
        <button
          type="button"
          onClick={() => window.location.reload()}
          style={{
            marginTop: "1.25rem",
            border: "none",
            cursor: "pointer",
            fontWeight: 700,
            color: "#04121d",
            padding: "0.7rem 1.4rem",
            borderRadius: "10px",
            background: "linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)",
          }}
        >
          Neu laden
        </button>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
}
interface ErrorBoundaryState {
  error: ErrInfo | null;
}

/** Fängt Render-Fehler in der Komponentenhierarchie ab. */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error: formatError(error) };
  }

  override componentDidCatch(error: unknown) {
    // Sichtbar in der Konsole für Entwickler.
    console.error("Orcmatic ErrorBoundary:", error);
  }

  override render() {
    if (this.state.error) {
      return (
        <OverlayBox
          title="Es ist ein Fehler aufgetreten"
          message={this.state.error.message}
          stack={this.state.error.stack}
        />
      );
    }
    return this.props.children;
  }
}

/**
 * Fängt nicht abgefangene Fehler und Promise-Rejections global ab und zeigt
 * sie als Overlay an (z. B. Fehler aus useEffect/async-Code).
 */
export function installGlobalErrorOverlay(): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as { __orcmaticOverlayInstalled?: boolean };
  if (w.__orcmaticOverlayInstalled) return;
  w.__orcmaticOverlayInstalled = true;

  const show = (err: unknown) => {
    const { message, stack } = formatError(err);
    let host = document.getElementById("orcmatic-error-overlay");
    if (!host) {
      host = document.createElement("div");
      host.id = "orcmatic-error-overlay";
      document.body.appendChild(host);
    }
    host.innerHTML = "";
    host.style.cssText =
      "position:fixed;inset:0;z-index:2147483647;background:#061b2a;color:#e6f4f7;" +
      "font-family:'Inter',system-ui,sans-serif;padding:1.5rem;overflow:auto;";
    const inner = document.createElement("div");
    inner.style.cssText = "max-width:720px;margin:2rem auto 0;";
    inner.innerHTML =
      '<div style="font-size:.78rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#22d3ee;margin-bottom:.75rem">Laufzeitfehler</div>' +
      '<p style="font-size:1.05rem;font-weight:600;margin:0 0 1rem;line-height:1.5"></p>' +
      (stack
        ? '<pre style="font-size:.78rem;line-height:1.5;white-space:pre-wrap;word-break:break-word;background:rgba(0,0,0,.35);border:1px solid rgba(230,244,247,.12);border-radius:12px;padding:1rem;margin:0;color:rgba(230,244,247,.8)"></pre>'
        : "");
    const p = inner.querySelector("p");
    if (p) p.textContent = message;
    const pre = inner.querySelector("pre");
    if (pre && stack) pre.textContent = stack;
    host.appendChild(inner);
  };

  window.addEventListener("error", (e) => show(e.error ?? e.message));
  window.addEventListener("unhandledrejection", (e) => show(e.reason));
}
